import { useState } from "react";
import { VaultOverview } from "./VaultOverview";
import { VaultCoverage } from "./VaultCoverage";
import { Addresses } from "./Addresses";
import { Geocode } from "./Geocode";

/** The address vault, all of it, behind one tab.
 *
 *  WHAT THIS REPLACED. The vault was spread across four places in the top nav and two more inside
 *  Geocodes: `Addresses`, `Placements`, `Geocodes → Vault coverage`, `Geocodes → Where to send`,
 *  plus the overview. Ten surfaces in the nav bar, and the two subjects were interleaved --
 *  Geocodes is the per-DISTRICT cleanup of voter households, while Vault coverage is a STATEWIDE
 *  scorecard of our own address data. They share the word "geocode" and nothing else: different
 *  scope, different table, different question, different person's job.
 *
 *  So the split is by SUBJECT, not by tool. This tab is the asset -- what the vault holds, how
 *  good it is, and the two queues of work that improve it. Geocodes is the workflow that cleans
 *  one district's households, and keeps its numbered 1-2-3 steps and its two lookup tools.
 *
 *  CORRECTED 2026-09-02, AND THE CORRECTION IS THE POINT. The split above was right about the two
 *  subjects and wrong about where they belong. It put them side by side in the COMPANY portal's top
 *  nav, which is the business back end -- client accounts, staff, billing, provisioning. Address
 *  data is ONE SUBJECT inside that, not two of its eight tabs.
 *
 *      "all of this work geocodes vaults placement blah blah blah, all of that is under one tab,
 *       all of that is one section of a broader company portal"  -- Caleb, 2026-09-02
 *
 *  So Geocodes moves in here as a sixth sub-tab and the top nav loses a tab. The subject split is
 *  KEPT, because it was correct and it was expensive to learn: the header still says which
 *  population each sub-tab touches, and the district picker still belongs to the voter-file cleanup
 *  and nothing else. What changed is the level it lives at, not the distinction.
 *
 *  THE SHELL OWNS THE HEADING (2026-09-11). Addresses and Geocode were top-level tabs and each
 *  still rendered its own <h2> after moving in here, so both drew TWO titles with TWO different
 *  subtitles stacked -- "Address vault / Queue: ..." above "Address geocodes / reviewed before any
 *  voter file ...". The inner ones are gone and this one now shows the SUB-TAB's own label, so the
 *  heading names the screen you are on rather than the section it lives in. VaultCoverage and
 *  VaultOverview never had the problem; they were built inside the shell.
 *
 *  THE ORDER IS THE ORDER THE QUESTIONS GET ASKED. What is in the vault, how good is it per
 *  county, and then the queues. A staffer who opens the queues first
 *  is working a list without knowing whether it is the biggest list.
 */

type Sub = "overview" | "coverage" | "addresses" | "voterfile";

/** Exported because App.tsx draws these in the left rail (2026-09-09). ONE list, in the file
 *  that owns the screens -- a second copy in the shell is a menu that goes stale the first time
 *  a screen is added and nobody thinks to look in two places. */
export const VAULT_SUBS: { v: Sub; label: string; hint: string }[] = [
  { v: "overview",   label: "Overview",         hint: "Every subtraction from 12.3M addresses down to homes" },
  { v: "coverage",   label: "Coverage by county", hint: "All 254 counties, with a verdict saying whose gap each gap is" },
  // WHERE TO SEND WAS HERE AND IS GONE (2026-09-11). It asked the right question -- of the doors
  // in a walkbook, which have the pin on the house -- and answered it from the wrong table.
  //
  // It scored public.household.geocode_quality, which arrives with the voter file. Measured on
  // production: households exist for ONE county (Bexar, 144,260, already 96.8% on a building), and
  // ZERO of them carry geocode_source = 'vault' -- so none of the vault's 869,155 building
  // placements, and nothing done to the vault this week, was ever visible on that screen. A
  // statewide-shaped table rendering one row, scoring a population the vault work does not reach.
  //
  // Coverage by county answers the same question properly: on_the_building, on_the_lot and
  // pct_on_a_building for all 254 counties, MEASURED by testing each pin against building
  // footprints rather than trusting a label. This was a worse duplicate of columns that already
  // exist, from the wrong source, for one county.
  //
  // NOTHING ELSE IS AFFECTED. The screen was a report -- one GET and a table, no buttons, no
  // writes -- so removing it cannot change a row. The voter file's own cleanup tool is
  // `voterfile` below and is untouched.
  { v: "addresses",  label: "Address review",   hint: "Queue: vault addresses whose pin the parcel check disputes — statewide, not the voter file" },
  // PLACEMENT REVIEW WAS HERE AND IS GONE (2026-09-11). It existed for one backlog -- the 893
  // placements the grading pass judged worse than the pin they replaced -- and that backlog is
  // cleared. The pass that produced every one of them was retired in 0245, so the queue cannot
  // refill: record_placement refuses method 'parcel' outright, and the building pass can only move
  // a pin ONTO a building, which is the one thing a loss of this shape is not.
  //
  // The alarm replaced the screen. grade_placements.py exits non-zero and prints a banner if it
  // ever finds one again -- news, rather than a queue somebody has to remember to work. A surface
  // whose only job is to render an empty state is furniture.
  //
  // address_vault.placement_review and placement_triage_queue are KEPT. The verdicts are a record
  // of what a person decided and record_placement now reads them: a 'withdrawn' address may not be
  // re-placed by any pass. Dropping the table to tidy the nav would quietly un-stick 862 decisions.
  // LAST, AND NAMED FOR ITS POPULATION. Everything above is the address vault -- 12.3M addresses,
  // statewide, ours. This one cleans the licensed VOTER FILE for one district, which is a different
  // table and a different job. Calling it "Geocodes" beside them is what let someone follow a
  // number from a vault screen into a queue with no relationship to it.
  { v: "voterfile",  label: "Voter-file cleanup", hint: "A district's voter households — the licensed file, not the address vault" },
];

export function Vault({ sub, onSub }: { sub: string; onSub: (v: string) => void }) {
  // A COUNTY CARRIED WITH THE SUB-TAB. Set by the overview when it sends you at a specific county's
  // queue, cleared as soon as you pick another sub-tab yourself -- a filter that outlives the click
  // that set it is a filter someone will later swear they never applied.
  const [fips, setFips] = useState<number | undefined>(undefined);
  const active = VAULT_SUBS.find((s) => s.v === sub) || VAULT_SUBS[0];

  return (
    <div className="wrap">
      <div className="row" style={{ marginBottom: 10, flexWrap: "wrap", alignItems: "baseline", gap: 10 }}>
        <h2 style={{ margin: 0 }}>{active.label}</h2>
        <span className="muted" style={{ fontSize: 13 }}>{active.hint}</span>
      </div>
      {/* MOUNTED ONE AT A TIME, on purpose. Each of these fetches on mount and two of them are the
          slowest requests in the portal; rendering all four and hiding three would pay for every
          one of them on every visit.

          Addresses was a top-level tab and still opens with its own `.wrap`
          (max-width 1150px, 20px padding), which nested inside this one would indent them by a
          gutter nothing else has and narrow them twice. Rather than reach into two screens of
          300-800 lines to strip a div, the nesting is cancelled here: negative margins undo the
          outer padding and `max-width:none` undoes the outer clamp, so the inner .wrap lands
          exactly where a top-level one did. One rule, in the file that caused the problem. */}
      {sub === "overview"   && <VaultOverview onGo={(v, f) => { setFips(f); onSub(v); }} />}
      {sub === "coverage"   && <VaultCoverage />}
      {(sub === "addresses" || sub === "voterfile") && (
        <div style={{ margin: "-20px", maxWidth: "none" }}>
          {sub === "addresses"  && <Addresses initialFips={fips} key={fips ?? "all"} />}
          {/* Geocode was a top-level tab and opens with its own .wrap, same as the two above, so it
              gets the same nesting cancellation rather than an edit to a 1,500-line screen. */}
          {sub === "voterfile"  && <Geocode />}
        </div>
      )}
    </div>
  );
}
